import { Link, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  GraduationCap,
  Video,
  BookOpen,
  FileText,
  CalendarRange,
  TrendingUp,
  Megaphone,
  User,
  LogOut,
  Users,
  Settings,
  ClipboardList,
  ShieldAlert,
  Type,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Icon3D } from "@/components/Icon3D";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

// Each link declares both the 3D asset name and a lucide fallback used when
// the 3D PNG is missing (e.g. local dev before the asset bundle ships).
const STUDENT_LINKS = [
  { to: "/student/dashboard",    icon3d: "dashboard",    fallback: LayoutDashboard, label: "Dashboard" },
  { to: "/student/course",       icon3d: "course",       fallback: GraduationCap,   label: "Course" },
  { to: "/student/recordings",   icon3d: "video",        fallback: Video,           label: "Class Recordings" },
  { to: "/student/modules",      icon3d: "book",         fallback: BookOpen,        label: "Learning Modules" },
  { to: "/student/notes",        icon3d: "document",     fallback: FileText,        label: "Class Notes" },
  { to: "/student/events",       icon3d: "calendar",     fallback: CalendarRange,   label: "Event Calendar" },
  { to: "/student/progress",     icon3d: "trending",     fallback: TrendingUp,      label: "Progress" },
  { to: "/student/announcements",icon3d: "megaphone",    fallback: Megaphone,       label: "Announcements" },
  { to: "/student/profile",      icon3d: "user",         fallback: User,            label: "Profile" },
];

const ADMIN_LINKS = [
  { to: "/admin",                icon3d: "dashboard",    fallback: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/students",       icon3d: "users",        fallback: Users,           label: "Students" },
  { to: "/admin/batches",        icon3d: "layers",       fallback: GraduationCap,   label: "Batches" },
  { to: "/admin/recordings",     icon3d: "video",        fallback: Video,           label: "Class Recordings" },
  { to: "/admin/modules",        icon3d: "book",         fallback: BookOpen,        label: "Learning Modules" },
  { to: "/admin/notes",          icon3d: "document",     fallback: FileText,        label: "Class Notes" },
  { to: "/admin/events",         icon3d: "calendar",     fallback: CalendarRange,   label: "Events" },
  { to: "/admin/announcements",  icon3d: "megaphone",    fallback: Megaphone,       label: "Announcements" },
  { to: "/admin/content",        icon3d: "type",         fallback: Type,            label: "Content (CMS)" },
  { to: "/admin/preview-videos", icon3d: "video",        fallback: Video,           label: "Preview Videos" },
  { to: "/admin/security",       icon3d: "shield",       fallback: ShieldAlert,     label: "Security" },
  { to: "/admin/reports",        icon3d: "clipboard",    fallback: ClipboardList,   label: "Reports" },
  { to: "/admin/settings",       icon3d: "settings",     fallback: Settings,        label: "Settings" },
];

export function Sidebar({ role = "student", onNavigate }) {
  const { logout, user } = useAuth();
  const links = role === "admin" ? ADMIN_LINKS : STUDENT_LINKS;

  const handleLogout = async () => {
    await logout();
    if (onNavigate) onNavigate();
    window.location.href = "/login";
  };

  return (
    <aside className="h-full flex flex-col glass-sidebar" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-border/40">
        <Logo size="sm" linkTo={role === "admin" ? "/admin" : "/student/dashboard"} />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {links.map(({ to, icon3d, fallback, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg pl-2 pr-3 py-2 text-sm font-medium transition-all duration-300 ${
                isActive
                  ? "nav-active"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:translate-x-[2px]"
              }`
            }
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon3D name={icon3d} size={28} fallback={fallback} alt="" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-border/40 space-y-2">
        {user && (
          <div className="px-3 py-2 rounded-lg bg-muted/30 border border-border/40">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Signed in as</p>
            <p className="text-sm font-medium truncate mt-0.5">{user.name || user.email}</p>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.75} />
          <span>Logout</span>
        </Button>
      </div>
    </aside>
  );
}
